import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/layout/PageHero';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const CONTACT_EMAIL = 'contact@autoxium.com';

// Keep in sync with what the backend actually stores (backend/db/models.py) and
// with the VPS log-rotation settings (nginx: 14 days, journald: 7 days).
const retention = [
  ['Account details (name, email, mobile, password hash)', 'While your account exists; deleted within 30 days of an account-deletion request'],
  ['Articles, questions, answers, votes, favorites, topic requests', 'While your account exists, or until you or a moderator delete them'],
  ['Email verification codes', 'Valid for 10 minutes; stored only in hashed form and replaced by the next code'],
  ['Feedback messages and content reports', 'Until handled, then up to 2 years to help prevent abuse'],
  ['Newsletter subscription', 'Until you ask to unsubscribe'],
  ['Web server logs (IP address, browser, pages requested)', '14 days'],
  ['Application logs', '7 days'],
  ['Page-visit counts for Trending sections', 'Kept indefinitely; contain no IP address or account link'],
];

const processors = [
  ['Hostinger International Ltd.', 'Hosts the website, database and the contact@autoxium.com mailbox', 'India (Mumbai data centre)'],
  ['Resend, Inc.', 'Delivers account emails (verification codes, password resets, answer notifications)', 'United States'],
];

// Table from 640px up; stacked cards on phones, where multi-column tables don't fit.
const DataTable = ({ head, rows }) => (
  <>
  <div className="not-prose my-6 space-y-3 sm:hidden">
    {rows.map((row) => (
      <dl key={row[0]} className="rounded-xl border border-structure bg-white p-4 text-sm space-y-1.5">
        <dt className="font-semibold text-foreground">{row[0]}</dt>
        {row.slice(1).map((cell, i) => (
          <dd key={i} className="text-muted-foreground">
            <span className="font-semibold text-foreground">{head[i + 1]}: </span>{cell}
          </dd>
        ))}
      </dl>
    ))}
  </div>
  <div className="not-prose my-6 rounded-xl border border-structure overflow-x-auto hidden sm:block">
    <Table>
      <TableHeader>
        <TableRow>
          {head.map((h) => <TableHead key={h}>{h}</TableHead>)}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row[0]}>
            {row.map((cell, i) => (
              <TableCell key={i} className="text-sm align-top min-w-[140px]">{cell}</TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
  </>
);

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen" data-testid="privacy-policy-page">
      <PageHero
        eyebrow="Legal"
        title="Privacy Policy"
        description="How AUTOXIUM collects, uses, and protects your information."
      />
      <section className="py-12 md:py-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl prose-autoxium">
            <p className="text-sm text-muted-foreground">Last updated: September 26, 2026</p>

            <h2>Who We Are</h2>
            <p>
              AUTOXIUM (autoxium.com) is operated by an individual based in India, referred to
              in this policy as <strong>the owner of autoxium.com</strong> ("we", "us"). The
              owner decides how and why your personal data is processed and is the Data
              Fiduciary under India's Digital Personal Data Protection Act, 2023 (and the
              "controller" under the EU/UK GDPR for visitors from those regions).
            </p>
            <p>
              Contact for all privacy matters:{' '}
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            </p>

            <h2>What Data We Collect</h2>
            <h3>Information you give us</h3>
            <ul>
              <li><strong>Account:</strong> first and last name, email address, optional mobile number, and your password (stored only as a secure one-way hash — we can never see it).</li>
              <li><strong>Content you post:</strong> articles, community questions and answers, votes, content reports, topic requests, and favorites.</li>
              <li><strong>Feedback:</strong> messages sent through the feedback widget, together with your name, email and the page you were on.</li>
              <li><strong>Newsletter:</strong> your email address (and name, if you were signed in) when you subscribe.</li>
              <li><strong>Emails to us:</strong> anything you choose to include when you write to {CONTACT_EMAIL}.</li>
            </ul>
            <h3>Information collected automatically</h3>
            <ul>
              <li><strong>Server logs:</strong> IP address, browser and device type, the pages requested and the time of the request, used to keep the site secure and working.</li>
              <li><strong>Security counters:</strong> failed login attempts, used to temporarily lock accounts under attack.</li>
              <li><strong>Anonymous visit counts:</strong> which Articles, topics, tools and simulations are opened, used to show Trending lists. These counts contain no IP address and are not linked to your account.</li>
              <li><strong>Browser storage:</strong> only what's strictly necessary to keep you signed in — see our <Link to="/cookie-policy">Cookie Policy</Link>.</li>
            </ul>
            <p>
              We do <strong>not</strong> use analytics, advertising or tracking services, and we
              do not collect payment, financial, government-ID, location, or vehicle data.
            </p>

            <h2>How and Why We Use Your Data</h2>
            <ul>
              <li><strong>To run your account and the platform</strong> — sign-in, email verification, password resets, publishing your content, and emailing you when someone answers your question. <em>Basis:</em> your consent given at registration, and performance of our Terms of Service.</li>
              <li><strong>To keep the platform safe</strong> — preventing spam, abuse and unauthorised access, and reviewing reported content. <em>Basis:</em> legitimate uses permitted by law (DPDP Act s.7) / legitimate interests (GDPR).</li>
              <li><strong>To improve the site</strong> — reading your feedback and the anonymous visit counts. <em>Basis:</em> consent / legitimate interests.</li>
              <li><strong>To send the newsletter</strong>, only if you subscribe. <em>Basis:</em> consent, which you can withdraw at any time.</li>
              <li><strong>To comply with the law</strong> — for example, responding to lawful requests from Indian authorities. <em>Basis:</em> legal obligation.</li>
            </ul>
            <p>We do not use your data for automated decision-making or profiling.</p>

            <h2>Who We Share It With</h2>
            <p>
              We do <strong>not sell, rent or share</strong> your personal data for advertising
              or any other commercial purpose. We use the following service providers
              (data processors), who process data only on our instructions:
            </p>
            <DataTable head={['Provider', 'What they do', 'Where']} rows={processors} />
            <p>
              Content you post publicly (articles, questions, answers) and your display name are
              visible to everyone. We may also disclose information when required by Indian law,
              a court order, or a lawful government request, or to protect the safety of users
              or the public.
            </p>

            <h2>International Transfers</h2>
            <p>
              Our website and database are hosted in India. Account emails are sent through
              Resend, whose servers are in the United States, so your name and email address
              pass through the US when we email you. Such transfers are permitted under the
              DPDP Act; for visitors from the EU/UK, we rely on our provider's standard
              contractual safeguards.
            </p>

            <h2>How Long We Keep It</h2>
            <DataTable head={['Data', 'Retention']} rows={retention} />

            <h2>Your Rights</h2>
            <p>Under the DPDP Act, 2023 (and the GDPR, if it applies to you), you can:</p>
            <ul>
              <li>get a summary of the personal data we hold about you and how it's used;</li>
              <li>correct or update your data — most details can be edited on your <Link to="/profile">Profile</Link> page;</li>
              <li>ask us to delete your account and personal data;</li>
              <li>withdraw consent at any time (for example, unsubscribe from the newsletter) — this doesn't affect processing that already took place;</li>
              <li>nominate another person to exercise your rights in the event of your death or incapacity;</li>
              <li>for GDPR users: receive your data in a portable format, object to or restrict processing, and complain to your local data protection authority.</li>
            </ul>
            <p>
              To exercise any right, email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>{' '}
              from the address linked to your account (so we can verify it's you). We respond
              within <strong>30 days</strong>. When an account is deleted, your personal details
              are erased; community posts you made are deleted, or anonymised where removing
              them would break other users' discussions.
            </p>

            <h2>Grievance Officer</h2>
            <p>
              In accordance with the Information Technology Act, 2000, its rules, and the DPDP
              Act, 2023, privacy complaints can be sent to:
            </p>
            <p>
              <strong>Grievance Officer:</strong> The owner of autoxium.com<br />
              <strong>Email:</strong> <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> (subject line "Grievance")<br />
              <strong>Location:</strong> India
            </p>
            <p>
              If you are not satisfied with our response, you may approach the Data Protection
              Board of India.
            </p>

            <h2>Security</h2>
            <p>
              The site is served only over HTTPS; passwords are hashed with bcrypt; sign-in and
              posting are rate-limited; and access to the database is restricted to the owner.
              No system is completely secure, but if a breach affecting your data occurs, we will
              notify you and the Data Protection Board as the law requires.
            </p>

            <h2>Children</h2>
            <p>
              You must be at least 18 years old to create an account. Articles, topics and tools
              can be read without an account. If you believe someone under 18 has registered,
              please email us and we will delete the account.
            </p>

            <h2>Changes to This Policy</h2>
            <p>
              If we make material changes — for example, adding a new service provider or any
              analytics — we will update this page and its date, and notify registered users by
              email before the change takes effect.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
